/**
 * Claude daily tracking: the pinned model, the request guardrails, the
 * vocabularies the pool and batch tables store, and the list prices the cost
 * ledger (`ClaudeBatchRequest.costUsd`) is computed from.
 *
 * Browser-safe on purpose (no SDK or database imports): the admin pool
 * control shows the same pin and prices the batch engine uses, so the two
 * can't drift. `llm-platforms.ts` reads the pin from here.
 *
 * ⚠️ PUBLIC DOCS DEPENDENCY: the pinned model is published in
 *   apps/web/content/docs/methodology/ai-visibility-data-collection.mdx
 * (and its .de.mdx counterpart). Changing it is a deliberate config change
 * plus those docs, in the same PR.
 */

/**
 * The one Claude model for discovery answers (Daily Prompt Tracking through
 * the Message Batches API, and AI Visibility Reports through the synchronous
 * path). Recorded on every batch and every answer.
 */
export const CLAUDE_DISCOVERY_MODEL = "claude-opus-5-5";

/**
 * Web search tool version sent with every discovery request. The basic
 * variant, as measured in exp 009 (research.spyglasses.io,
 * claude-api-vs-claude-ai). The newer dynamic-filtering variant runs code
 * execution under the hood, which changes cost and behavior: moving to it is
 * a methodology change, not a bump.
 */
export const CLAUDE_WEB_SEARCH_TOOL_TYPE = "web_search_20250305" as const;

/** `output_config.effort` values the discovery requests send. */
export type ClaudeDiscoveryEffort = "low" | "medium" | "high";

/**
 * Guardrails for a Claude TRACKING request (batch path). Exp 009 measured 440
 * batch calls: at most 5 searches, at most 2,881 output tokens. Medium effort
 * is claude.ai's default reasoning setting. AI Visibility Reports keep their
 * own synchronous settings (see discovery-query-execution.ts).
 */
export const CLAUDE_TRACKING_REQUEST = {
	/** `web_search` `max_uses`. */
	maxSearches: 5,
	/** `max_tokens`. */
	maxTokens: 8000,
	effort: "medium",
} as const satisfies {
	maxSearches: number;
	maxTokens: number;
	effort: ClaudeDiscoveryEffort;
};

/* ------------------------------------------------------------------ */
/* Stored vocabularies                                                 */
/* ------------------------------------------------------------------ */

/** `PlatformPool.platform` / `ProjectPlatformSetting.platform` for Claude. */
export const CLAUDE_POOL_PLATFORM = "claude" as const;

/** `PlatformPool.billingSource`. */
export const PLATFORM_POOL_BILLING_SOURCES = ["manual", "stripe"] as const;
export type PlatformPoolBillingSource =
	(typeof PLATFORM_POOL_BILLING_SOURCES)[number];

/** `ClaudeBatch.kind`. */
export const CLAUDE_BATCH_KINDS = ["nightly", "day1", "retry"] as const;
export type ClaudeBatchKind = (typeof CLAUDE_BATCH_KINDS)[number];

/** `ClaudeBatch.status`. */
export const CLAUDE_BATCH_STATUSES = [
	"submitted",
	"ended",
	"canceled",
	"failed_to_submit",
] as const;
export type ClaudeBatchStatus = (typeof CLAUDE_BATCH_STATUSES)[number];

/** `ClaudeBatchRequest.status`. */
export const CLAUDE_BATCH_REQUEST_STATUSES = [
	"pending",
	"submitted",
	"succeeded",
	"errored",
	"expired",
	"canceled",
	"failed",
] as const;
export type ClaudeBatchRequestStatus =
	(typeof CLAUDE_BATCH_REQUEST_STATUSES)[number];

/* ------------------------------------------------------------------ */
/* List prices and the cost of one run                                 */
/* ------------------------------------------------------------------ */

/** USD per million tokens, standard (non-batch) rate. */
export interface ClaudeModelPrices {
	input: number;
	output: number;
	/** 5-minute cache write: 1.25x input. */
	cacheWrite5m: number;
	/** 1-hour cache write: 2x input. */
	cacheWrite1h: number;
	cacheRead: number;
}

/**
 * Published list prices per model (claude-api skill, cached 2026-10-06; the
 * same table exp 009's harness priced with). Every model the tracking can
 * send must be here: a test pins `CLAUDE_DISCOVERY_MODEL`.
 */
export const CLAUDE_LIST_PRICES: Readonly<Record<string, ClaudeModelPrices>> = {
	"claude-opus-5-5": {
		input: 4,
		output: 20,
		cacheWrite5m: 5,
		cacheWrite1h: 8,
		cacheRead: 0.2,
	},
};

/**
 * Web search: $10 per 1,000 searches. Billed per search, and the Batches
 * discount does not apply to it.
 */
export const CLAUDE_WEB_SEARCH_USD_PER_SEARCH = 0.01;

/**
 * Message Batches bill every token category (cache reads and writes too) at
 * this share of the standard rate. Published rate (50% off), not yet
 * confirmed on an invoice with batch traffic.
 */
export const CLAUDE_BATCH_TOKEN_MULTIPLIER = 0.5;

/**
 * Prices for `model`. A dated snapshot resolves to its alias by the longest
 * matching prefix (`claude-opus-5-5-20261001` → `claude-opus-5-5`). Throws
 * for a model with no price, so a pin change without a price fails loudly.
 */
export function claudeListPrices(model: string): ClaudeModelPrices {
	const exact = CLAUDE_LIST_PRICES[model];
	if (exact) return exact;
	const alias = Object.keys(CLAUDE_LIST_PRICES)
		.filter((key) => model.startsWith(`${key}-`))
		.sort((a, b) => b.length - a.length)[0];
	if (!alias) {
		throw new Error(`No Claude list price for model "${model}"`);
	}
	return CLAUDE_LIST_PRICES[alias] as ClaudeModelPrices;
}

/**
 * The parts of a Messages API `usage` object the cost reads (the SDK's
 * `Usage` satisfies it).
 */
export interface ClaudeUsageLike {
	input_tokens?: number | null;
	output_tokens?: number | null;
	cache_read_input_tokens?: number | null;
	cache_creation_input_tokens?: number | null;
	cache_creation?: {
		ephemeral_5m_input_tokens?: number | null;
		ephemeral_1h_input_tokens?: number | null;
	} | null;
	server_tool_use?: { web_search_requests?: number | null } | null;
}

/** A run's cost by component, in USD. */
export interface ClaudeRunCost {
	input: number;
	output: number;
	cacheWrite5m: number;
	cacheWrite1h: number;
	cacheRead: number;
	webSearch: number;
	total: number;
}

function count(value: number | null | undefined): number {
	return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * List-price cost of one Claude call (or of a summed usage across calls
 * billed at the same rate), by component. `batch` halves the token
 * components; searches are never discounted. Cache writes without a TTL
 * split are priced as 5-minute writes.
 */
export function claudeRunCostBreakdown(
	usage: ClaudeUsageLike | null | undefined,
	options: { batch: boolean; model?: string },
): ClaudeRunCost {
	const prices = claudeListPrices(options.model ?? CLAUDE_DISCOVERY_MODEL);
	const multiplier = options.batch ? CLAUDE_BATCH_TOKEN_MULTIPLIER : 1;
	const perToken = (tokens: number, usdPerMillion: number) =>
		(tokens * usdPerMillion * multiplier) / 1_000_000;

	const totalWrite = count(usage?.cache_creation_input_tokens);
	let write5m = count(usage?.cache_creation?.ephemeral_5m_input_tokens);
	let write1h = count(usage?.cache_creation?.ephemeral_1h_input_tokens);
	if (write5m + write1h === 0 && totalWrite > 0) {
		write5m = totalWrite;
		write1h = 0;
	}

	const cost = {
		input: perToken(count(usage?.input_tokens), prices.input),
		output: perToken(count(usage?.output_tokens), prices.output),
		cacheWrite5m: perToken(write5m, prices.cacheWrite5m),
		cacheWrite1h: perToken(write1h, prices.cacheWrite1h),
		cacheRead: perToken(
			count(usage?.cache_read_input_tokens),
			prices.cacheRead,
		),
		webSearch:
			count(usage?.server_tool_use?.web_search_requests) *
			CLAUDE_WEB_SEARCH_USD_PER_SEARCH,
	};
	return {
		...cost,
		total:
			cost.input +
			cost.output +
			cost.cacheWrite5m +
			cost.cacheWrite1h +
			cost.cacheRead +
			cost.webSearch,
	};
}

/**
 * List-price USD cost of one Claude call, rounded to the micro-dollar (what
 * the ledger stores). For a batch result continued after `pause_turn`, add
 * the batch call at `{ batch: true }` and the continuation usage at
 * `{ batch: false }` (`continueClaudePauseTurn` returns them separately).
 */
export function claudeRunCostUsd(
	usage: ClaudeUsageLike | null | undefined,
	options: { batch: boolean; model?: string },
): number {
	return roundUsd(claudeRunCostBreakdown(usage, options).total);
}

/** Round to the micro-dollar (six decimals). */
export function roundUsd(value: number): number {
	return Math.round(value * 1_000_000) / 1_000_000;
}
